import { QueryResult } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { ServerErrors } from 'dash-common';
import {
  localStorage,
  ModalContext,
  toast,
  useServerResponseTranslation,
} from 'dash-ui';
import {
  GetPortfoliosByPublicFolderQuery,
  GetPortfolioFirstPublicPropertyQuery,
  GetPortfolioFirstPublicPropertyQueryVariables,
  GetPortfoliosByPublicFolderQueryVariables,
} from 'gql/generated.graphql';
import { useNavigate } from 'react-router-dom';
import { RoutesPath } from 'flow-components/routes/routes';

/** effect to open OpenPortfolioWithPassword when:
 *  - GetPortfoliosByPublicFolderQuery ot GetPropertyInPublicBrokerPortfolioQuery
 *  return PASSWORD_IS_REQUIRED_FOR_PORTFOLIO respond
 *  and save password to local storage for next page visit
 * */
const useOpenPortfolioWithPasswordModal = (
  queryProps: QueryResult<
    GetPortfoliosByPublicFolderQuery | GetPortfolioFirstPublicPropertyQuery,
    | GetPortfoliosByPublicFolderQueryVariables
    | GetPortfolioFirstPublicPropertyQueryVariables
  >,
  queryVariables: any
) => {
  const translateErrorMessage = useServerResponseTranslation();
  const [loading, setLoading] = useState(false);
  const { error, refetch } = queryProps;
  const { isOpenCurrent, toggleModal, setPayload } = useContext(ModalContext);
  const isPasswordModalOpen = isOpenCurrent['OpenPortfolioWithPasswordModal'];
  const navigate = useNavigate();
  // submit password form
  const onSubmit = (password: string) => {
    localStorage.setPortfolioPassword(password);
    setLoading(true);
    refetch({
      ...queryVariables,
      password,
    })
      .then(() => setLoading(false))
      .catch((error) => {
        setLoading(false);
        toast.error(
          translateErrorMessage(error, ['common:brokerPortalServerErrors'])
        );
      });
  };

  useEffect(() => {
    setPayload({ onSubmit, loading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (
      [
        ServerErrors.PORTFOLIO_NOT_EXISTS.toString(),
        ServerErrors.PORTFOLIO_IS_PRIVATE.toString(),
        ServerErrors.PROPERTY_IS_PRIVATE.toString(),
      ].includes(error?.message as any)
    ) {
      navigate(RoutesPath.UnavailablePortfolioPage);
      return;
    }

    if (
      error &&
      ![
        ServerErrors.INCORRECT_PASSWORD,
        ServerErrors.PASSWORD_IS_REQUIRED_FOR_PORTFOLIO,
      ].includes(error?.message as any)
    ) {
      navigate(RoutesPath.UnavailablePortfolioPage);
      return;
    }
    // handle case when password in local storage is incorrect

    // check if password exists in local storage before showing modal
    if (error?.message === ServerErrors.PASSWORD_IS_REQUIRED_FOR_PORTFOLIO) {
      const password = localStorage.getPortfolioPassword();
      if (password) {
        try {
          refetch({
            ...queryVariables,
            password,
          });
        } catch (err: any) {
          if (err?.message === ServerErrors.INCORRECT_PASSWORD) {
            return;
          }
          throw err;
        }
      } else {
        toggleModal(false, 'ViewAllPortfolioPropertiesModal');
        // show modal
        toggleModal(true, 'OpenPortfolioWithPasswordModal');
      }
    } // FIXME: re-check next line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const password = localStorage.getPortfolioPassword();
    if (password) {
      refetch({
        ...queryVariables,
        password,
      }).catch((error) => {
        if (error?.message === ServerErrors.INCORRECT_PASSWORD) {
          localStorage.setPortfolioPassword('');
          toast.error(
            translateErrorMessage(error, ['common:brokerPortalServerErrors'])
          );
          toggleModal(true, 'OpenPortfolioWithPasswordModal');
        }
      });
    } else {
      toggleModal(false, 'ViewAllPortfolioPropertiesModal');
      // show modal
      toggleModal(true, 'OpenPortfolioWithPasswordModal');
    }
  }, []);

  // hide modal
  // FIXME: re-check next line
  useEffect(() => {
    if (isPasswordModalOpen && !error) {
      toggleModal(false, 'OpenPortfolioWithPasswordModal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isPasswordModalOpen]);
};

export default useOpenPortfolioWithPasswordModal;
